import React, { useEffect, useRef, useState, useMemo } from 'react';
import * as d3 from 'd3';
import { Shield, Users, Key, ChevronRight } from 'lucide-react';

export interface RbacNode {
    id: string;
    name: string;
    type: 'root' | 'role' | 'permission';
    children?: RbacNode[];
    roleId?: string;
    collapsed?: boolean;
    parent?: RbacNode; // Added by d3 hierarchy
    x?: number; // Added by d3 tree
    y?: number; // Added by d3 tree
}

interface RbacTheme {
    border: string;
    bg: string;
    shadow: string;
    text: string;
}

interface RbacTreeDiagramProps {
    rootNode: RbacNode | null;
    roleThemes: RbacTheme[];
    onRoleClick?: (node: RbacNode) => void;
    onToggleCollapse?: (roleId: string) => void;
    selectedNodeId?: string | null;
    initialTranslate?: { x: number; y: number };
    initialScale?: number;
    backgroundGradient?: string;
    gridColor?: string;
    glowFilterId?: string; // Unique ID for SVG filter
}

// Layout constants
const HORIZONTAL_SPACING = 380;
const VERTICAL_SPACING = 70;

const NODE_WIDTHS: Record<string, number> = {
    root: 200,
    role: 180,
    permission: 200
};

const NODE_HEIGHTS: Record<string, number> = {
    root: 60,
    role: 50,
    permission: 36
};

// Helper: Bezier curve path
const createLinkPath = (x1: number, y1: number, x2: number, y2: number): string => {
    const midX = (x1 + x2) / 2;
    const yDiff = y2 - y1;
    const minCurve = 15;
    const offset = Math.abs(yDiff) < 5 ? minCurve : 0;
    return `M ${x1} ${y1} C ${midX} ${y1 + offset}, ${midX} ${y2 - offset}, ${x2} ${y2}`;
};

export const RbacTreeDiagram: React.FC<RbacTreeDiagramProps> = ({
    rootNode,
    roleThemes,
    onRoleClick,
    onToggleCollapse,
    selectedNodeId,
    initialTranslate = { x: 80, y: 300 },
    initialScale = 0.85,
    backgroundGradient,
    gridColor = 'rgba(148, 163, 184, 0.1)',
    glowFilterId = 'rbac-glow'
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [translate, setTranslate] = useState(initialTranslate);
    const [scale, setScale] = useState(initialScale);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // D3 Layout Calculation
    const { nodes, links } = useMemo(() => {
        if (!rootNode) return { nodes: [], links: [] };

        const root = d3.hierarchy<RbacNode>(rootNode, d => d.children);

        const treeLayout = d3.tree<RbacNode>()
            .nodeSize([VERTICAL_SPACING, HORIZONTAL_SPACING])
            .separation((a, b) => a.parent === b.parent ? 1.2 : 2.5);

        treeLayout(root);

        return {
            nodes: root.descendants() as (d3.HierarchyNode<RbacNode> & { x: number; y: number })[],
            links: root.links()
        };
    }, [rootNode]);

    // Handlers
    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        const zoomSensitivity = 0.001;
        const newScale = Math.min(Math.max(0.3, scale - e.deltaY * zoomSensitivity), 2);
        setScale(newScale);
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if ((e.target as HTMLElement).closest('.rbac-node')) return;
        setIsDragging(true);
        setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isDragging) {
            setTranslate({
                x: e.clientX - dragStart.x,
                y: e.clientY - dragStart.y,
            });
        }
    };

    const handleMouseUp = () => setIsDragging(false);

    const handleReset = () => {
        setTranslate(initialTranslate);
        setScale(initialScale);
    };

    const getRoleTheme = (node: d3.HierarchyNode<RbacNode>) => {
        // Find the "Role" ancestor or self to determine color
        let roleIndex = 0;
        const roleNodes = nodes.filter(n => n.data.type === 'role');

        // Strategy: If node is permission, align color with its parent role
        if (node.data.type === 'role') {
            roleIndex = roleNodes.findIndex(n => n.data.id === node.data.id);
        } else if (node.parent && node.parent.data.type === 'role') {
            roleIndex = roleNodes.findIndex(n => n.data.id === node.parent!.data.id);
        }

        if (roleIndex < 0) roleIndex = 0;
        return roleThemes[roleIndex % roleThemes.length];
    };

    return (
        <div className="flex flex-col h-full relative w-full overflow-hidden">
            {/* Controls */}
            <div className="absolute top-4 right-4 z-20 flex gap-2">
                <button
                    onClick={() => setScale(s => Math.min(s + 0.1, 2))}
                    className="w-8 h-8 bg-slate-800/90 border border-slate-600 rounded text-white hover:bg-slate-700 transition-colors flex items-center justify-center backdrop-blur-sm shadow-lg"
                >+</button>
                <button
                    onClick={() => setScale(s => Math.max(s - 0.1, 0.3))}
                    className="w-8 h-8 bg-slate-800/90 border border-slate-600 rounded text-white hover:bg-slate-700 transition-colors flex items-center justify-center backdrop-blur-sm shadow-lg"
                >−</button>
                <div className="w-px h-8 bg-slate-600/50 mx-1"></div>
                <button
                    onClick={handleReset}
                    className="px-3 h-8 bg-slate-800/90 border border-slate-600 rounded text-white text-xs hover:bg-slate-700 transition-colors backdrop-blur-sm shadow-lg"
                >Reset</button>
            </div>

            {/* Canvas */}
            <div
                ref={containerRef}
                className={`flex-1 relative overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} select-none`}
                style={{ background: '#0f172a' }}
                onWheel={handleWheel}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                {/* Backgrounds */}
                {backgroundGradient && (
                    <div className="absolute inset-0 pointer-events-none">
                        <div className="absolute inset-0" style={{ background: backgroundGradient }} />
                    </div>
                )}
                <div
                    className="absolute inset-0 opacity-20 pointer-events-none"
                    style={{
                        backgroundImage: `
                            linear-gradient(${gridColor} 1px, transparent 1px),
                            linear-gradient(90deg, ${gridColor} 1px, transparent 1px)
                        `,
                        backgroundSize: '40px 40px'
                    }}
                />

                {/* Transform Container */}
                <div
                    className="absolute top-0 left-0 origin-top-left transition-transform duration-100"
                    style={{
                        transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`
                    }}
                >
                    {/* SVG Links */}
                    <svg
                        className="absolute top-0 left-0 pointer-events-none overflow-visible"
                        style={{ width: 1, height: 1 }}
                    >
                        <defs>
                            <filter id={glowFilterId} x="-50%" y="-50%" width="200%" height="200%">
                                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                <feMerge>
                                    <feMergeNode in="coloredBlur" />
                                    <feMergeNode in="SourceGraphic" />
                                </feMerge>
                            </filter>
                        </defs>
                        {links.map((link, idx) => {
                            const source = link.source as (d3.HierarchyNode<RbacNode> & { x: number; y: number });
                            const target = link.target as (d3.HierarchyNode<RbacNode> & { x: number; y: number });

                            const sourceW = NODE_WIDTHS[source.data.type] || 200;
                            const targetW = NODE_WIDTHS[target.data.type] || 200;

                            // x=vertical, y=horizontal in d3.tree default, but we swapped in useMemo logic?
                            // No, typically tree returns x/y.
                            // In original code: left: node.y - nodeW/2, top: node.x - nodeH/2
                            // So x is Top (Vertical), y is Left (Horizontal).
                            // Link: Source Right -> Target Left

                            const startX = source.y + (sourceW / 2);
                            const startY = source.x;
                            const endX = target.y - (targetW / 2);
                            const endY = target.x;

                            // Determine color
                            // If source is root, using a default valid color (e.g. emerald/cyan)
                            // If source is role, use role theme.
                            let strokeColor = '#94a3b8'; // default slate-400

                            if (source.data.type === 'root') {
                                strokeColor = '#10b981'; // Emerald
                            } else {
                                const theme = getRoleTheme(source);
                                strokeColor = theme.border;
                            }

                            return (
                                <path
                                    key={idx}
                                    d={createLinkPath(startX, startY, endX, endY)}
                                    stroke={strokeColor}
                                    strokeWidth={2}
                                    fill="none"
                                    filter={`url(#${glowFilterId})`}
                                    opacity={0.8}
                                />
                            );
                        })}
                    </svg>

                    {/* Nodes */}
                    {nodes.map(node => {
                        const { type, name, roleId, id } = node.data;
                        const w = NODE_WIDTHS[type];
                        const h = NODE_HEIGHTS[type];
                        const theme = getRoleTheme(node);
                        const isRoot = type === 'root';
                        const isRole = type === 'role';
                        const isPerm = type === 'permission';
                        const isSelected = selectedNodeId === id;
                        const isCollapsed = node.data.collapsed;

                        return (
                            <div
                                key={id}
                                className="rbac-node absolute transition-all duration-200"
                                style={{
                                    left: node.y - w / 2,
                                    top: node.x - h / 2,
                                    width: w,
                                    height: h,
                                }}
                            >
                                <div
                                    onClick={(e) => {
                                        if (isRole && onRoleClick) {
                                            e.stopPropagation();
                                            onRoleClick(node.data);
                                        }
                                    }}
                                    className={`
                                        w-full h-full rounded-lg flex items-center justify-between px-3
                                        border-2 transition-all duration-200
                                        ${isRole ? 'cursor-pointer hover:scale-[1.02]' : ''}
                                        ${isSelected ? 'ring-2 ring-white/50 scale-105' : ''}
                                    `}
                                    style={{
                                        background: isRoot
                                            ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.3), rgba(6, 182, 212, 0.2))'
                                            : theme.bg,
                                        borderColor: isRoot ? '#10b981' : theme.border,
                                        boxShadow: `0 0 20px ${isRoot ? 'rgba(16, 185, 129, 0.3)' : theme.shadow}`
                                    }}
                                >
                                    {/* Left Dot */}
                                    <div
                                        className="absolute -left-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2"
                                        style={{
                                            backgroundColor: isRoot ? '#10b981' : theme.border,
                                            borderColor: isRoot ? '#10b981' : theme.border
                                        }}
                                    />

                                    {/* Content */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {isRoot && <Shield size={18} className="text-emerald-400 flex-shrink-0" />}
                                        {isRole && <Users size={16} style={{ color: theme.border }} className="flex-shrink-0" />}
                                        {isPerm && <Key size={14} style={{ color: theme.border }} className="flex-shrink-0" />}

                                        <span className={`truncate ${isRoot
                                            ? 'text-sm font-semibold text-emerald-100'
                                            : isRole
                                                ? 'text-sm font-medium text-slate-100'
                                                : 'text-xs text-slate-300'
                                            }`}>
                                            {name}
                                        </span>
                                    </div>

                                    {/* Toggle */}
                                    {isRole && onToggleCollapse && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (roleId) onToggleCollapse(roleId);
                                            }}
                                            className="p-1 rounded hover:bg-white/10 transition-colors"
                                        >
                                            <ChevronRight
                                                size={16}
                                                className={`transition-transform duration-200 ${node.data.collapsed ? '' : 'rotate-90'}`}
                                                style={{ color: theme.border }}
                                            />
                                        </button>
                                    )}

                                    {/* Right Dot */}
                                    <div
                                        className="absolute -right-1.5 top-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full border-2"
                                        style={{
                                            backgroundColor: isRoot ? '#10b981' : theme.border,
                                            borderColor: isRoot ? '#10b981' : theme.border
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
